// /components/ApplicationModal.jsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toastError, toastLoading, updateToast } from "../Common/toastHelper";
import FileUploadRow from "./FileUploadRow";
import { api } from "../../config";

const headingStyle = { fontFamily: "'Roboto', sans-serif", fontWeight: 400, color: "#99191b", fontSize: 'inherit' };
const paragraphStyle = { fontFamily: "'Roboto', sans-serif", fontWeight: 300, lineHeight: 1.5, margin: '0.2em 0 0.8em 0' };

// Maps the API role key (uppercase) to the i18n namespace key
const ROLE_I18N_MAP = {
    "TIROCINIO":                "internship",
    "ARCHITETTO":               "architect",
    "PROGETTISTA IMP. ELETTRICI": "electricalDesigner",
    "PROGETTISTA IMP. MECCANICI": "mechanicalDesigner"
};

function RoleDescription({ i18nKey, t }) {
    return (
        <>
            <b style={headingStyle}>{t('careers.modal.roleLabel')}</b>
            <p style={paragraphStyle}>{t(`careers.modal.${i18nKey}.roleDesc`)}</p>
            <b style={headingStyle}>{t('careers.modal.requirementsLabel')}</b>
            <p style={paragraphStyle}>{t(`careers.modal.${i18nKey}.requirementsDesc`)}</p>
            <b style={headingStyle}>{t('careers.modal.offerLabel')}</b>
            <p style={paragraphStyle}>{t(`careers.modal.${i18nKey}.offerDesc`)}</p>
        </>
    );
}

export function ApplicationModal({ open, onClose, role, onSubmit }) {
    const { t } = useTranslation();
    const [cvName, setCvName] = useState("");
    const [portfolioName, setPortfolioName] = useState("");

    if (!open) return null;

    const i18nKey = ROLE_I18N_MAP[role];
    const title = i18nKey ? t(`careers.modal.${i18nKey}.title`) : t('careers.modal.positionFallback');

    const validateFile = (file, label, required) => {
        if (!file) return required ? `${label}: ${t('careers.modal.validation.fileRequired')}` : null;

        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) return `${label}: ${t('careers.modal.validation.fileTooLarge')}`;

        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const allowed = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
        if (!allowed.includes(ext)) return `${label}: ${t('careers.modal.validation.invalidFormat')}`;

        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const form = e.currentTarget;
        const cvFile = form?.elements?.cv?.files?.[0] || null;
        const portfolioFile = form?.elements?.portfolio?.files?.[0] || null;

        const errCv = validateFile(cvFile, 'CV', true);
        if (errCv) return toastError(errCv);

        const errPort = validateFile(portfolioFile, 'Portfolio', false);
        if (errPort) return toastError(errPort);

        const fd = new FormData(form);
        fd.append('role', role);

        const loadingToast = toastLoading(t('careers.modal.submitting'));

        try {
            const res = await fetch(api('/sendApplication.php'), {
                method: 'POST',
                body: fd
            });

            const text = await res.text();
            let result;
            try {
                result = JSON.parse(text);
            } catch {
                throw new Error(t('careers.modal.errors.invalidResponse'));
            }

            if (!res.ok || !result?.success) {
                throw new Error(result?.message || `HTTP Error: ${res.status}`);
            }

            updateToast(loadingToast, result.message || t('careers.modal.submitSuccess'), 'success');
            onSubmit();
        } catch (err) {
            updateToast(loadingToast, `${t('careers.modal.errors.invalidResponse')}: ${err.message}`, 'error');
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-application">
                <button className="modal-close-btn" onClick={onClose} aria-label={t('common.close')}>
                    &times;
                </button>
                <h2 className="modal-title">{title}</h2>
                <div className="modal-content">
                    <div className="modal-role">
                        <div className="modal-role-desc">
                            {i18nKey
                                ? <RoleDescription i18nKey={i18nKey} t={t} />
                                : <p>{t('careers.modal.noDescription')}</p>
                            }
                        </div>
                    </div>
                    <form className="modal-form" autoComplete="on" onSubmit={handleSubmit}>
                        <div className="modal-form-row">
                            <div className="modal-form-field">
                                <label>{t('careers.modal.form.firstName')}</label>
                                <input type="text" name="nome" required />
                            </div>
                            <div className="modal-form-field">
                                <label>{t('careers.modal.form.lastName')}</label>
                                <input type="text" name="cognome" required />
                            </div>
                        </div>
                        <div className="modal-form-row">
                            <div className="modal-form-field">
                                <label>{t('careers.modal.form.email')}</label>
                                <input type="email" name="email" required />
                            </div>
                            <div className="modal-form-field">
                                <label>{t('careers.modal.form.phone')}</label>
                                <input type="tel" name="tel" required />
                            </div>
                        </div>
                        <div className="modal-form-field">
                            <label>{t('careers.modal.form.education')}</label>
                            <input type="text" name="education" />
                        </div>
                        <div className="modal-form-field">
                            <label>{t('careers.modal.form.experiences')}</label>
                            <textarea name="experiences" rows="3" />
                        </div>
                        <div className="modal-form-row">
                            <div className="modal-form-field">
                                <label>{t('careers.modal.form.cvLabel')}</label>
                                <FileUploadRow
                                    buttonLabel={t('careers.modal.form.selectCv')}
                                    name="cv"
                                    required
                                    value={cvName}
                                    onChangeName={setCvName}
                                    placeholder={t('careers.modal.form.filePlaceholder')}
                                />
                            </div>

                            <div className="modal-form-field">
                                <label>{t('careers.modal.form.portfolioLabel')}</label>
                                <FileUploadRow
                                    buttonLabel={t('careers.modal.form.selectPortfolio')}
                                    name="portfolio"
                                    value={portfolioName}
                                    onChangeName={setPortfolioName}
                                    placeholder={t('careers.modal.form.filePlaceholder')}
                                />
                            </div>
                        </div>
                        <button className="send-btn" type="submit">{t('careers.modal.form.submit')}</button>
                    </form>
                </div>
            </div>
        </div>
    );
}
